import querystring from 'query-string'
import set from 'lodash/set'
import get from 'lodash/get'
import merge from 'lodash/merge'
import omit from 'lodash/omit'

const initialQueryString = globalThis.location.search

export function populateFromQueryParams<T>(
  state: T,
  map: Map<string, string>,
  initialQuery: string = initialQueryString
): T {
  initialQuery = initialQuery.replace('%23', '#').replace('#', '&#')
  const queryObject: any = addTypesToQueryParams(querystring.parse(initialQuery))

  map.forEach((value, key) => {
    if (queryObject[value]) {
      if (key === 'purchase.loanTerms' || key === 'refinance.loanTerms') {
        // TODO: validation
        // @ts-ignore
        state = set(state as unknown as object, key, queryObject[value].split(',')) as unknown as T
      } else {
        let urlValue = queryObject[value]
        /*
         * Note: Validation only for URL, see dimensions.ts for ui level validation checks
         * If invalid ULR override, set back to default to  (preventing invalid urls from crashing our app)
         */
        if (key === 'user.creditScore' && (urlValue > 850 || urlValue < 350)) {
          urlValue = 780
        } else if (key === 'mortgageType' && !['Refinance', 'Purchase'].includes(urlValue)) {
          urlValue = 'Refinance'
        } else if (
          key === 'user.veteranStatus' &&
          ![
            'NoMilitaryService',
            'RegularMilitary',
            'Reserves',
            'NationalGuard',
            'MilitarySpouse',
            'ReserveSpouse',
          ].includes(urlValue)
        ) {
          urlValue = 'NoMilitaryService'
        } else if (key === 'user.points' && !['All', 'Zero', 'ZeroToOneInclusive', 'OneToTwo'].includes(urlValue)) {
          urlValue = 'All'
        } else if (key === 'user.hadPriorVaLoan' && !['true', 'false'].includes(urlValue)) {
          urlValue = false
        } else if (key === 'user.hasVaDisabilities' && !['true', 'false'].includes(urlValue)) {
          urlValue = false
        } else if (key === 'user.firstTimeHomebuyer' && !['true', 'false'].includes(urlValue)) {
          urlValue = false
        } else if (key === 'user.fha' && !['true', 'false'].includes(urlValue)) {
          urlValue = false
        } else if (key === 'user.lowUpfrontCosts' && !['true', 'false'].includes(urlValue)) {
          urlValue = false
        } else if (key === 'user.lowPayment' && !['true', 'false'].includes(urlValue)) {
          urlValue = false
        } else if (
          (key === 'purchase.propertyType' || key === 'refinance.propertyType') &&
          ![
            'SingleFamily',
            'Townhouse',
            'Condo4OrFewerStories',
            'Condo5OrMoreStories',
            'Coop',
            'MobileOrManufactured',
            'Modular',
            'Leasehold',
            'MultiFamily2Units',
            'MultiFamily3Units',
            'MultiFamily4Units',
            'Pud',
          ].includes(urlValue)
        ) {
          urlValue = 'SingleFamily'
        } else if (
          (key === 'purchase.propertyUse' || key === 'refinance.propertyUse') &&
          !['PrimaryResidence', 'SecondaryOrVacation', 'InvestmentOrRental'].includes(urlValue)
        ) {
          urlValue = 'PrimaryResidence'
        }
        state = set(state as unknown as object, key, urlValue) as unknown as T
      }
    }
  })

  return state
}

export function updateQueryParams<T>(
  state: T,
  map: Map<string, string>,
  initialQuery: string = initialQueryString
): void {
  let initialQueryObject = querystring.parse(initialQuery)
  let currentQueryObject = querystring.parse(globalThis.location.search)
  let newQueryObject = {}

  const refinanceQueryParams = [
    'refinanceLoanAmount',
    'refinancePropertyValue',
    'refinanceCashOutAmount',
    'refinancePropertyType',
    'refinancePropertyUse',
    'refinancePoints',
    'refinanceLoanTerms',
  ]
  const purchaseQueryParams = [
    'purchasePrice',
    'purchaseDownPayment',
    'purchasePropertyType',
    'purchasePropertyUse',
    'purchasePoints',
    'purchaseLoanTerms',
  ]

  if (get(state, 'mortgageType') === 'Purchase') {
    state = omit(state, ['refinance'])
    initialQueryObject = omit(initialQueryObject, refinanceQueryParams)
    currentQueryObject = omit(currentQueryObject, refinanceQueryParams)
  } else if (get(state, 'mortgageType') === 'Refinance') {
    state = omit(state, ['purchase'])
    initialQueryObject = omit(initialQueryObject, purchaseQueryParams)
    currentQueryObject = omit(currentQueryObject, purchaseQueryParams)
  }

  map.forEach((value, key) => {
    if (key === 'purchase.loanTerms' || key === 'refinance.loanTerms') {
      newQueryObject = set(newQueryObject, value, get(state, key)?.join(','))
    } else {
      newQueryObject = set(newQueryObject, value, get(state, key))
    }
  })

  newQueryObject = merge(initialQueryObject, currentQueryObject, newQueryObject)
  const newQuery = querystring.stringify(newQueryObject)

  globalThis.history.replaceState(
    null,
    document.title,
    `${globalThis.location.origin}${globalThis.location.pathname}?${newQuery}`
  )
}

export function addTypesToQueryParams<T>(state: object): T {
  let typedState: any = {}
  for (const [key, value] of Object.entries(state)) {
    if (typeof value === 'object' && value !== null && key !== 'loanTerms') {
      typedState[key] = { ...addTypesToQueryParams(value) }
    } else {
      // zip code doesn't fit this rule
      // attempt string to number
      if (value === 'true') {
        typedState[key] = true
      } else if (value === 'false') {
        typedState[key] = false
      } else if (isNumber(value) && key !== 'zipCode') {
        typedState[key] = Number(value)
      } else {
        typedState[key] = value
      }
    }
  }

  return typedState as T
}

const isNumber = (value: any) => value !== null && !isNaN(Number(value))
